import type { ModalOptions } from '~/stores/modal.store';

export const CONFIRM_MODAL = 'confirm-modal';

// Singleton resolver for the currently open confirm dialog. Only ever set and
// consumed on the client, in response to a user action, so a single shared
// instance across the app is fine (and intended — see the ConfirmModal mounted
// once in app.vue).
let resolver: ((confirmed: boolean) => void) | null = null;

/**
 * Promise-based confirm dialog. Usage:
 *   if (await confirm({ title, message, variant: 'info' })) { ...do it... }
 * A single <modal-confirm-modal> (in app.vue) renders the result.
 */
export function useConfirmModal() {
    const modalStore = useModalStore();

    const confirm = (options: ModalOptions): Promise<boolean> => {
        // If a previous dialog somehow never settled, decline it first.
        resolver?.(false);
        modalStore.openModal(CONFIRM_MODAL, options);
        return new Promise<boolean>((resolve) => {
            resolver = resolve;
        });
    };

    // Called by ConfirmModal when the dialog closes.
    const settle = (confirmed: boolean) => {
        resolver?.(confirmed);
        resolver = null;
    };

    return { CONFIRM_MODAL, confirm, settle };
}
